"use client";

import { useEffect, useRef } from "react";
import type { EquityCurvePoint } from "@/types/portfolio-builder.types";

interface Props {
  data: EquityCurvePoint[];
  totalEquity: number;
}

export function EquityCurveChart({ data, totalEquity }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || data.length < 2) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const pad = { top: 20, right: 16, bottom: 30, left: 60 };
    const plotW = w - pad.left - pad.right;
    const plotH = h - pad.top - pad.bottom;

    const values = data.map((d) => d.value);
    const minVal = Math.min(totalEquity, ...values);
    const maxVal = Math.max(totalEquity, ...values);
    const range = maxVal - minVal || 1;

    const xScale = (i: number) => pad.left + (i / (data.length - 1)) * plotW;
    const yScale = (v: number) => pad.top + plotH - ((v - minVal) / range) * plotH;

    // Clear
    ctx.clearRect(0, 0, w, h);

    // Grid lines
    ctx.strokeStyle = "#1f2937";
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const y = pad.top + (plotH / 4) * i;
      ctx.beginPath();
      ctx.moveTo(pad.left, y);
      ctx.lineTo(w - pad.right, y);
      ctx.stroke();
    }

    // Initial investment line
    const baseY = yScale(totalEquity);
    ctx.strokeStyle = "#4b5563";
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(pad.left, baseY);
    ctx.lineTo(w - pad.right, baseY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Equity curve
    ctx.strokeStyle = "#3b82f6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    data.forEach((d, i) => {
      const x = xScale(i);
      const y = yScale(d.value);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();

    // Fill gradient under curve
    const gradient = ctx.createLinearGradient(0, pad.top, 0, pad.top + plotH);
    gradient.addColorStop(0, "rgba(59, 130, 246, 0.15)");
    gradient.addColorStop(1, "rgba(59, 130, 246, 0)");
    ctx.fillStyle = gradient;
    ctx.beginPath();
    data.forEach((d, i) => {
      const x = xScale(i);
      const y = yScale(d.value);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.lineTo(xScale(data.length - 1), pad.top + plotH);
    ctx.lineTo(pad.left, pad.top + plotH);
    ctx.closePath();
    ctx.fill();

    // Y-axis labels
    ctx.fillStyle = "#6b7280";
    ctx.font = "11px sans-serif";
    ctx.textAlign = "right";
    for (let i = 0; i <= 4; i++) {
      const val = minVal + (range / 4) * (4 - i);
      const y = pad.top + (plotH / 4) * i;
      const label =
        val >= 1_000_000
          ? `$${(val / 1_000_000).toFixed(1)}M`
          : `$${(val / 1_000).toFixed(0)}K`;
      ctx.fillText(label, pad.left - 8, y + 4);
    }

    // X-axis labels (show ~5 evenly spaced dates)
    ctx.textAlign = "center";
    const labelCount = Math.min(5, data.length);
    for (let i = 0; i < labelCount; i++) {
      const idx = Math.floor((i / (labelCount - 1)) * (data.length - 1));
      const x = xScale(idx);
      ctx.fillText(data[idx].date, x, h - 8);
    }
  }, [data, totalEquity]);

  if (data.length < 2) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-gray-500">
        Insufficient data for equity curve
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold">Portfolio Growth</h3>
      <canvas
        ref={canvasRef}
        className="h-56 w-full rounded-lg border border-gray-800 bg-gray-900/50"
        style={{ width: "100%", height: 224 }}
      />
    </div>
  );
}
